import { FeatureSmallCard } from 'components/ui/FeatureSmallCard';
import { SectionLabel } from 'components/ui/SectionLabel';
import { useLanguage } from 'context/language.context';
import { StringKey } from 'dictionaries/string-key.enum';
import { motion, useMotionValue } from 'motion/react';
import { useEffect } from 'react';

export const FeaturesSection = () => {
  const motionCursorX = useMotionValue(0);
  const motionCursorY = useMotionValue(0);

  const { t, direction } = useLanguage();
  const featuresSmallCards = [
    {
      image: <img src="assets/events-icon.png" alt="events" />,
      text: t(StringKey.EVENTS),
    },
    {
      image: <img src="assets/budget-icon.png" alt="budget" />,
      text: t(StringKey.BUDGET),
    },
    {
      image: <img src="assets/social-icon.png" alt="social" />,
      text: t(StringKey.SOCIAL),
    },
    {
      image: <img src="assets/tenders-icon.png" alt="tenders" />,
      text: t(StringKey.TENDERS),
    },
    {
      image: <img src="assets/invitations-icon.png" alt="invitations" />,
      text: t(StringKey.INVITATIONS),
    },
    {
      image: <img src="assets/tickets-icon.png" alt="tickets" />,
      text: t(StringKey.TICKETS),
    },
    {
      image: <img src="assets/teams-icon.png" alt="teams" />,
      text: t(StringKey.TEAMS),
    },
    {
      image: <img src="assets/calendar-icon.png" alt="calendar" />,
      text: t(StringKey.CALENDAR),
    },
  ];

  const featuresLargeCards = [
    {
      image: (
        <img
          className="grow object-cover object-top"
          src={`assets/tabs-image${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="tabs-image"
        />
      ),
      text: t(StringKey.LARGE_CARD_TEXT_1),
    },
    {
      image: (
        <img
          className="grow object-cover object-top"
          src={`assets/socials-image${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="socials-image"
        />
      ),
      text: t(StringKey.LARGE_CARD_TEXT_2),
    },
    {
      image: (
        <img
          className="grow object-cover object-top"
          src={`assets/stats-image${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="stats-image"
        />
      ),
      text: t(StringKey.LARGE_CARD_TEXT_3),
    },
    {
      image: (
        <img
          className="grow object-cover object-top"
          src={`assets/roles-image${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="roles-image"
        />
      ),
      text: t(StringKey.LARGE_CARD_TEXT_4),
    },
    {
      image: (
        <img
          className="grow object-cover object-top"
          src={`assets/contracts-image${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="contracts-image"
        />
      ),
      text: t(StringKey.LARGE_CARD_TEXT_5),
    },
    {
      image: (
        <img
          className="grow object-cover object-top"
          src={`assets/comments-image${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="comments-image"
        />
      ),
      text: t(StringKey.LARGE_CARD_TEXT_6),
    },
  ];

  useEffect(() => {
    const updateCursorPositionHander = (e: MouseEvent) => {
      motionCursorX.set(e.clientX);
      motionCursorY.set(e.clientY);
    };

    window.addEventListener('mousemove', updateCursorPositionHander);

    return () => {
      window.removeEventListener('mousemove', updateCursorPositionHander);
    };
  }, [motionCursorX, motionCursorY]);

  return (
    <section
      id="features"
      className="relative w-full overflow-x-hidden flex flex-col bg-[#08070C] px-4 py-[80px] gap-[80px] md:gap-[150px] md:px-20 md:py-[150px]"
    >
      <div className="sm:px-8">
        <div className="flex flex-col items-center lg:flex-row lg:items-start gap-6 md:gap-8">
          <div className="relative lg:max-w-[calc(100%-452px)]">
            <SectionLabel>{t(StringKey.FEATURES_SECTION_LABEL_1)}</SectionLabel>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ ease: 'easeOut', duration: 0.6, delay: 0 }}
            >
              <h3 className="bg-clip-text bg-label-gradient mt-2 font-semibold text-[30px] leading-[38px] md:text-4xl text-transparent">
                {t(StringKey.FEATURES_SECTION_TITLE_1)}
              </h3>
              <p className="mt-3">{t(StringKey.FEATURES_SECTION_DESC_1)}</p>
            </motion.div>
          </div>
          <div dir={'ltr'} className="relative lg:w-[420px] grid grid-cols-4 gap-4 xs:p-6">
            <img
              src="assets/pattern-bg.png"
              alt="pattern-bg"
              className="absolute flex justify-self-center top-2 xs:top-7 object-center object-cover h-full xs:h-[83%]"
            />
            {featuresSmallCards.map(({ image, text }, index) => (
              <FeatureSmallCard
                key={index}
                text={text}
                image={image}
                motionCursorX={motionCursorX}
                motionCursorY={motionCursorY}
              />
            ))}
          </div>
        </div>
        <div className="mt-11 md:mt-16 flex flex-wrap justify-center xl:justify-between gap-8">
          {featuresLargeCards.slice(0, 3).map(({ image, text }, index) => (
            <motion.div
              initial={{ opacity: 0, x: 150 + 100 * index }}
              whileInView={{
                opacity: 1,
                x: 0,
                transition: { ease: 'easeOut', duration: 0.6, delay: 0.1 + 0.2 * index },
              }}
              whileHover={{
                opacity: 1,
                rotate: -4,
                transition: { ease: 'easeOut', duration: 0.3, delay: 0 },
              }}
              transition={{ type: 'tween' }}
              key={index}
              className="flex flex-col xl:flex-1 justify-between w-full md:w-[calc(50%-16px)] max-w-96 aspect-[1/0.96] rounded-2xl bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)] border border-[#333333]"
            >
              {image}
              <p className="font-semibold text-center text-sm md:text-m mx-3 mb-6 md:mx-4 md:mb-8">
                {text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="flex flex-col items-center lg:items-start sm:px-8">
        <div className="relative lg:max-w-[80%]">
          <SectionLabel>{t(StringKey.FEATURES_SECTION_LABEL_2)}</SectionLabel>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ ease: 'easeOut', duration: 0.6, delay: 0 }}
          >
            <h3 className="bg-clip-text bg-label-gradient mt-2 font-semibold text-[30px] leading-[38px] md:text-4xl text-transparent">
              {t(StringKey.FEATURES_SECTION_TITLE_2)}
            </h3>
            <p className="mt-3 lg:max-w-[80%]">{t(StringKey.FEATURES_SECTION_DESC_2)}</p>
          </motion.div>
        </div>
        <div className="w-full mt-11 md:mt-16 flex flex-wrap justify-center xl:justify-between gap-8">
          {featuresLargeCards.slice(3).map(({ image, text }, index) => (
            <motion.div
              initial={{ opacity: 0, x: 150 + 100 * index }}
              whileInView={{
                opacity: 1,
                x: 0,
                transition: { ease: 'easeOut', duration: 0.6, delay: 0.1 + 0.2 * index },
              }}
              whileHover={{
                opacity: 1,
                rotate: -4,
                transition: { ease: 'easeOut', duration: 0.3, delay: 0 },
              }}
              transition={{ type: 'tween' }}
              key={index}
              className="flex flex-col xl:flex-1 justify-between w-full md:w-[calc(50%-16px)] max-w-96 aspect-[1/0.96] rounded-2xl bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)] border border-[#333333]"
            >
              {image}
              <p className="font-semibold text-center text-sm md:text-m mx-3 mb-6 md:mx-4 md:mb-8">
                {text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
